/**
 * ProfileStats.jsx — sub-page hosting the SmarterHubStats panel plus
 * the writer-only Journey card. Extracted from Profile.jsx during the
 * Feb 26 2026 restructure so the main Profile page stays light.
 *
 * Feb 14 2026 — user asked to "replace every trace of all the old
 * stats with the new stats panel". Removed the duplicate inline
 * Reading Stats / Scribe Stats 4-tile grids that duplicated the same
 * numbers already surfaced by SmarterHubStats. Kept the "Journey to
 * Top Scribe" card since it's a unique milestone-ledger card, not a
 * stats duplication.
 */
import React, { useEffect, useState } from 'react';
import { BarChart3, Crown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import ProfileSubPage from '../components/ProfileSubPage';
import SmarterHubStats from '../components/SmarterHubStats';
import StreakHeroCard from '../components/StreakHeroCard';
import { checkAuth, authFetch } from '../utils/authUtils';
import { getActiveSpace, onSpaceChange, effectiveRoleFor } from '../utils/spaceContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProfileStats = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  // Journey snapshot: lifetime words + achievements count + coins from
  // journey. Only fetched for writers/admins so we don't churn the API
  // on reader views.
  const [journey, setJourney] = useState(null);
  // Re-render whenever the writer↔reader (or admin viewing-as) axis
  // changes so the streak card swaps in real time without navigation.
  const [, setSpaceTick] = useState(0);

  useEffect(() => onSpaceChange(() => setSpaceTick((t) => t + 1)), []);

  useEffect(() => {
    (async () => {
      const u = await checkAuth();
      if (!u) { navigate('/login'); return; }
      setUser(u);
      if (u.role === 'writer' || u.role === 'admin') {
        try {
          const jr = await authFetch(`${BACKEND_URL}/api/writer/journey`);
          if (jr.ok) setJourney(await jr.json());
        } catch (_) { /* non-fatal */ }
      }
    })();
  }, [navigate]);

  const effective = user ? effectiveRoleFor(user.role, getActiveSpace()) : 'user';
  const showWriterJourney = effective === 'writer';

  return (
    <ProfileSubPage title="My Stats" icon={BarChart3} accent="cyan">
      <StreakHeroCard role={showWriterJourney ? 'writer' : 'reader'} />

      <SmarterHubStats isAdmin={user?.role === 'admin'} allowToggle />

      {/* Journey milestones — surfaces published-word coin rewards
          alongside the rest of the scribe stats so the gamification
          loop is visible here AND on the dedicated /writer-journey
          page. Unique card — not a stats duplication. */}
      {showWriterJourney && journey && (
        <Card
          className="bg-gradient-to-br from-amber-900/30 via-slate-800/40 to-purple-900/30 border border-amber-500/30"
          data-testid="profile-journey-card"
        >
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-300" /> Journey to Top Scribe
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 bg-slate-900/40 rounded-lg">
                <p className="text-[10px] uppercase tracking-widest text-amber-200/80 font-bold">Lifetime</p>
                <p className="text-xl font-black text-white tabular-nums" data-testid="profile-journey-lifetime">
                  {Number(journey.lifetime_words || 0).toLocaleString()}
                </p>
                <p className="text-[10px] text-slate-400">words</p>
              </div>
              <div className="text-center p-3 bg-slate-900/40 rounded-lg">
                <p className="text-[10px] uppercase tracking-widest text-emerald-200/80 font-bold">Milestones</p>
                <p className="text-xl font-black text-emerald-300 tabular-nums" data-testid="profile-journey-count">
                  {(journey.achievements || []).length}
                </p>
                <p className="text-[10px] text-slate-400">earned</p>
              </div>
              <div className="text-center p-3 bg-slate-900/40 rounded-lg">
                <p className="text-[10px] uppercase tracking-widest text-amber-200/80 font-bold">Coins</p>
                <p className="text-xl font-black text-amber-300 tabular-nums" data-testid="profile-journey-coins">
                  +{Number(journey.total_coins_from_journey || 0).toLocaleString()}
                </p>
                <p className="text-[10px] text-slate-400">from journey</p>
              </div>
            </div>
            {journey.next_milestone && (
              <p className="text-xs text-slate-300">
                Next: <span className="text-amber-200 font-bold">{Number(journey.next_milestone.threshold_words).toLocaleString()} words</span>
                {' '}→ <span className="text-amber-200 font-bold">+{journey.next_milestone.coins_reward} coins</span>
                {' '}<span className="text-slate-500">({Number(journey.next_milestone.remaining).toLocaleString()} to go)</span>
              </p>
            )}
            <Button
              onClick={() => navigate('/writer-journey')}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="profile-journey-cta"
            >
              <Crown className="w-4 h-4 mr-2" /> Open Journey Page
            </Button>
          </CardContent>
        </Card>
      )}
    </ProfileSubPage>
  );
};

export default ProfileStats;
